import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { ProjectView } from '../types'
import { dayTotal, gridTotal, rowTotal, type GridRow } from '../lib/grid'
import { cn } from '@/lib/utils'
import { Select } from '@/components/ui/select'

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function dayNumber(isoDate: string): string {
  return isoDate.slice(8, 10)
}

/** The 7-column hour grid. Purely presentational — the page owns the rows. */
export function WeekGrid({
  rows,
  dates,
  projects,
  editable,
  onChange,
}: {
  rows: GridRow[]
  dates: string[]
  projects: ProjectView[]
  editable: boolean
  onChange: (rows: GridRow[]) => void
}) {
  function updateRow(key: string, patch: Partial<GridRow>) {
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)))
  }

  function setHours(key: string, date: string, raw: string) {
    const row = rows.find((candidate) => candidate.key === key)
    if (!row) return
    const hours = { ...row.hours }
    // An empty cell means "nothing worked", which is not the same as an explicit zero —
    // keeping zeros would post rows the server rejects for having no hours.
    if (raw.trim() === '') delete hours[date]
    else hours[date] = Number(raw)
    updateRow(key, { hours })
  }

  function addRow() {
    onChange([
      ...rows,
      { key: `new-${Date.now()}-${rows.length}`, projectId: '', notes: '', hours: {} },
    ])
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto overscroll-x-contain">
        <table className="w-full min-w-[720px] text-sm">
          <caption className="sr-only">Hours worked per project per day</caption>
          <thead>
            <tr className="border-b border-[var(--color-border)] text-xs text-[var(--color-muted)]">
              <th scope="col" className="px-3 py-2 text-start font-medium">Project</th>
              {dates.map((date, index) => (
                <th key={date} scope="col" className="px-2 py-2 text-center font-medium">
                  <span className="block">{DAY_LABELS[index]}</span>
                  <span className="block tabular-nums text-[var(--color-body)]">{dayNumber(date)}</span>
                </th>
              ))}
              <th scope="col" className="px-3 py-2 text-end font-medium">Total</th>
              <th scope="col" className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-b border-[var(--color-border)]">
                <td className="px-3 py-2">
                  <Select
                    aria-label="Project"
                    disabled={!editable}
                    value={row.projectId}
                    onChange={(event) => updateRow(row.key, { projectId: event.target.value })}
                    className="w-full min-w-40"
                  >
                    <option value="">Choose a project…</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                        {project.clientName ? ` — ${project.clientName}` : ''}
                      </option>
                    ))}
                  </Select>
                </td>

                {dates.map((date) => (
                  <td key={date} className="px-1 py-2">
                    <Input
                      type="number"
                      inputMode="decimal"
                      min={0}
                      max={24}
                      step={0.25}
                      disabled={!editable}
                      aria-label={`Hours on ${date}`}
                      value={row.hours[date] ?? ''}
                      onChange={(event) => setHours(row.key, date, event.target.value)}
                      className="h-9 w-16 px-1 text-center tabular-nums"
                    />
                  </td>
                ))}

                <td className="px-3 py-2 text-end font-medium tabular-nums text-[var(--color-heading)]">
                  {rowTotal(row) || ''}
                </td>
                <td className="px-2 py-2 text-end">
                  {editable && (
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Remove row"
                      onClick={() => onChange(rows.filter((candidate) => candidate.key !== row.key))}
                    >
                      <Trash2 aria-hidden="true" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}

            {rows.length === 0 && (
              <tr>
                <td colSpan={dates.length + 3} className="px-3 py-6 text-center text-[var(--color-muted)]">
                  No hours recorded for this week yet.
                </td>
              </tr>
            )}
          </tbody>

          <tfoot>
            <tr className="text-sm">
              <th scope="row" className="px-3 py-2 text-start font-medium text-[var(--color-muted)]">
                Daily total
              </th>
              {dates.map((date) => {
                const total = dayTotal(rows, date)
                return (
                  <td
                    key={date}
                    className={cn(
                      'px-2 py-2 text-center tabular-nums',
                      total > 24 ? 'text-[var(--color-danger)]' : 'text-[var(--color-heading)]',
                    )}
                  >
                    {total || '—'}
                  </td>
                )
              })}
              <td className="px-3 py-2 text-end font-semibold tabular-nums text-[var(--color-heading)]">
                {gridTotal(rows)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      {editable && (
        <Button variant="outline" size="sm" onClick={addRow}>
          <Plus aria-hidden="true" />
          Add project
        </Button>
      )}
    </div>
  )
}
